import { useRef, useState, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useQuestions } from '@/contexts/QuestionsContext';
import { useDiagramData } from '@/hooks/useDiagramData';
import { questionsService } from '@/services/questionsService';
import { RateLimitError } from '@/lib/errors';

export interface QuestionsGenerationState {
  generateError: string | null;
  isParsing: boolean;
  parseError: string | null;
  analyzeError: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

export interface QuestionsGenerationActions {
  handleGenerateQuestions: (isaContext?: string) => Promise<boolean>;
  handleParseFile: (file: File) => Promise<void>;
  handleAnalyze: () => Promise<void>;
  handleFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setGenerateError: (err: string | null) => void;
  setParseError: (err: string | null) => void;
  setAnalyzeError: (err: string | null) => void;
}

export function useQuestionsGeneration(
  currentProjectId: number,
  selectedDiagramIndices: Set<number>,
) {
  const { diagrams, isProject, projectInfo } = useApp();
  const {
    setQuestions,
    quickScanResults,
    setQuickScanResults,
    invalidateQuestions,
    isGeneratingQuestions: isGenerating,
    setIsGeneratingQuestions: setIsGenerating,
    isAnalyzingQuestionnaire: isAnalyzing,
    setIsAnalyzingQuestionnaire: setIsAnalyzing,
  } = useQuestions();
  const { currentDiagram } = useDiagramData();

  // ── Generate state ──────────────────────────────────────────────────────────
  const [generateError, setGenerateError] = useState<string | null>(null);

  // ── Parse state (step 1: load xlsx without AI) ──────────────────────────────
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Analyze state (step 2: AI analysis) ─────────────────────────────────────
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleGenerateQuestions = useCallback(async (isaContext?: string): Promise<boolean> => {
    if (isGenerating || !currentProjectId) return false;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      let entities, paths, boundaries;
      if (isProject && diagrams.length > 1) {
        const selected = diagrams.filter((_, i) => selectedDiagramIndices.has(i));
        if (selected.length === 0) { setIsGenerating(false); return false; }
        entities = selected.flatMap((d) => d.entities ?? []);
        paths = selected.flatMap((d) => d.paths ?? []);
        boundaries = selected.flatMap((d) => d.boundaries ?? []);
      } else {
        if (!currentDiagram) { setIsGenerating(false); return false; }
        entities = currentDiagram.entities;
        paths = currentDiagram.paths;
        boundaries = currentDiagram.boundaries;
      }
      const classification = projectInfo
        ? {
            confidentiality: projectInfo.confidentiality ?? undefined,
            integrity: projectInfo.integrity ?? undefined,
            availability: projectInfo.availability ?? undefined,
            internet_facing: projectInfo.internet_facing ?? undefined,
          }
        : undefined;
      const result = await questionsService.generate(currentProjectId, {
        entities, paths, boundaries,
        ...classification,
        ...(isaContext ? { isa_context: isaContext } : {}),
      });
      setQuestions(result.questions);
      setQuickScanResults({});
      return true;
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Question generation failed');
      return false;
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, currentProjectId, isProject, diagrams, selectedDiagramIndices, currentDiagram, projectInfo, setIsGenerating, setQuestions, setQuickScanResults]);

  const handleParseFile = useCallback(async (file: File) => {
    if (!currentProjectId) return;
    setIsParsing(true);
    setParseError(null);
    setAnalyzeError(null);
    try {
      const result = await questionsService.importXlsx(currentProjectId, file);
      setQuestions(result.questions);
      setQuickScanResults({});
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Failed to import questionnaire file');
    } finally {
      setIsParsing(false);
    }
  }, [currentProjectId, setQuestions, setQuickScanResults]);

  const handleAnalyze = useCallback(async () => {
    if (!currentDiagram || isAnalyzing || !currentProjectId) return;
    setIsAnalyzing(true);
    setAnalyzeError(null);
    try {
      const result = await questionsService.quickScan(currentProjectId, {
        entities: currentDiagram.entities,
        paths: currentDiagram.paths,
        boundaries: currentDiagram.boundaries,
      });
      setQuickScanResults(
        result.findings.reduce(
          (acc, f) => ({ ...acc, [f.question_id]: f }),
          { ...quickScanResults }
        )
      );
      // Backend already sets manual_status during quick scan — refresh questions to pick it up
      await invalidateQuestions();
    } catch (err) {
      setAnalyzeError(
        err instanceof RateLimitError
          ? `Rate limit reached. Please wait ${err.retryAfter ? `${err.retryAfter}s` : 'a moment'} before running Quick Scan again.`
          : err instanceof Error ? err.message : 'Quick scan failed'
      );
    } finally {
      setIsAnalyzing(false);
    }
  }, [currentDiagram, isAnalyzing, currentProjectId, quickScanResults, setIsAnalyzing, setQuickScanResults, invalidateQuestions]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleParseFile(file);
    e.target.value = '';
  }, [handleParseFile]);

  const state: QuestionsGenerationState = {
    generateError,
    isParsing,
    parseError,
    analyzeError,
    fileInputRef,
  };

  const actions: QuestionsGenerationActions = {
    handleGenerateQuestions,
    handleParseFile,
    handleAnalyze,
    handleFileInput,
    setGenerateError,
    setParseError,
    setAnalyzeError,
  };

  return { state, actions };
}
